import { useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Edit, FileText, ExternalLink, Download, Plus, Trash2, Upload, FolderUp, FileArchive, RefreshCw } from "lucide-react";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import { BatchAccessField } from "@/components/BatchAccessField";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_NOTE = {
  id: null,
  code: "",
  title: "",
  description: "",
  file_url: "",
  original_filename: "",
  display_name: "",
  file_size: 0,
  folder_name: "",
  status: "belum_dimuat_naik",
  published: true,
  visibility: "all_students",
  allowed_batches: [],
};

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-MY", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function AdminNotes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const filesRef = useRef(null);
  const folderRef = useRef(null);
  const zipRef = useRef(null);
  const pdfRef = useRef(null);

  const load = () => api.get("/notes").then((r) => setItems(r.data));
  const [batches, setBatches] = useState([]);
  useEffect(() => {
    load();
    api.get("/admin/batches").then((r) => setBatches(r.data)).catch(() => setBatches([]));
  }, []);

  const openEdit = (n) => { setEditing({ ...EMPTY_NOTE, ...n }); setOpen(true); };
  const openCreate = () => { setEditing({ ...EMPTY_NOTE }); setOpen(true); };

  const save = async () => {
    if (!editing.code?.trim() || !editing.title?.trim()) {
      toast.error("Please isi Kod & Tajuk.");
      return;
    }
    try {
      const payload = {
        code: editing.code,
        title: editing.title,
        description: editing.description,
        file_url: editing.file_url,
        original_filename: editing.original_filename,
        display_name: editing.display_name,
        file_size: editing.file_size,
        folder_name: editing.folder_name,
        status: editing.status,
        published: editing.published,
        visibility: editing.visibility || "all_students",
        allowed_batches: editing.allowed_batches || [],
      };
      if (editing.id) {
        await api.patch(`/admin/notes/${editing.id}`, payload);
        toast.success("Note updated.");
      } else {
        await api.post("/admin/notes", payload);
        toast.success("Note added.");
      }
      setOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save";
      toast.error(typeof msg === "string" ? msg : "Failed to save");
    }
  };

  const removeNote = async (id, title) => {
    if (!window.confirm(`Delete note "${title}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/admin/notes/${id}`);
      toast.success("Note deleted.");
      load();
    } catch { toast.error("Gagal padam."); }
  };

  const removePdfFromEditing = () => {
    setEditing({
      ...editing,
      file_url: "",
      original_filename: "",
      display_name: "",
      file_size: 0,
      status: "belum_dimuat_naik",
    });
    toast.message("PDF removed. Click Save to confirm.");
  };

  const onPickPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are accepted.");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "note");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing({
        ...editing,
        file_url: fileUrl(data.id),
        original_filename: data.original_filename || file.name,
        display_name: editing.display_name || data.original_filename || file.name,
        file_size: data.size || file.size,
        status: editing.status === "belum_dimuat_naik" ? "tersedia" : editing.status,
      });
      toast.success("PDF uploaded successfully.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed.";
      toast.error(typeof msg === "string" ? msg : "Upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onBulkPick = async (e, mode) => {
    const fileList = Array.from(e.target.files || []);
    if (fileList.length === 0) return;
    const pdfs = fileList.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length === 0) {
      toast.error("No PDF found in your selection.");
      e.target.value = "";
      return;
    }
    // Derive folder name from webkitdirectory paths
    let folderName = "";
    if (mode === "folder" && fileList[0].webkitRelativePath) {
      folderName = fileList[0].webkitRelativePath.split("/")[0] || "";
    }
    setBusy(true);
    try {
      const fd = new FormData();
      pdfs.forEach((f) => fd.append("files", f, f.name));
      if (folderName) fd.append("folder_name", folderName);
      const { data } = await api.post("/admin/notes/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = data?.created?.length || 0;
      const s = data?.skipped?.length || 0;
      toast.success(`${c} notes added${s ? ` · ${s} skipped` : ""}.`);
      setBulkOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bulk upload failed.";
      toast.error(typeof msg === "string" ? msg : "Bulk upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onZipPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please choose a ZIP file only.");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/notes/zip", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = data?.created?.length || 0;
      const s = data?.skipped?.length || 0;
      toast.success(`${c} notes from ZIP "${data?.folder_name}"${s ? ` · ${s} skipped` : ""}.`);
      setBulkOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Pemerolehan ZIP gagal.";
      toast.error(typeof msg === "string" ? msg : "Pemerolehan ZIP gagal.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const testOpen = async (n, mode) => {
    if (!n.file_url) {
      toast.error(mode === "inline" ? "PDF has not been uploaded for this module." : "No file to download.");
      return;
    }
    try {
      const { data } = await api.post(`/notes/${n.id}/download`, null, { params: { mode } });
      if (!data?.download_url) {
        toast.error("PDF link could not be generated.");
        return;
      }
      const url = data.download_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${data.download_url}` : data.download_url;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Gagal membuka PDF.");
    }
  };

  return (
    <AdminLayout title="Class Notes">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Class Notes</h1>
          <p className="mt-1 text-muted-foreground">Upload PDF notes for each module.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-upload-btn">
            <FolderUp className="h-4 w-4" /> Upload Pukal
          </Button>
          <Button onClick={openCreate} data-testid="add-note-btn" className="btn-glow">
            <Plus className="h-4 w-4" /> Add New Note
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((n) => (
          <Card key={n.id} className="glass-card" data-testid={`admin-note-${n.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-mono font-semibold text-primary">{n.code}</span>
                  <h3 className="font-heading font-semibold text-sm mt-0.5 truncate">{n.title}</h3>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <StatusBadge status={n.file_url ? (n.status === "tersedia" ? "tersedia" : n.status) : "belum_dimuat_naik"} />
                    {n.folder_name && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground truncate">📁 {n.folder_name}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 rounded-lg border border-border bg-muted/20 p-2.5 text-xs space-y-0.5">
                {n.file_url ? (
                  <>
                    <p className="truncate font-medium" title={n.display_name || n.original_filename || "—"}>
                      {n.display_name || n.original_filename || "Fail PDF"}
                    </p>
                    <p className="text-muted-foreground">
                      {formatBytes(n.file_size)} · {formatDate(n.updated_at || n.created_at)}
                    </p>
                  </>
                ) : (
                  <p className="text-muted-foreground">No file · not yet uploaded</p>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" disabled={!n.file_url} onClick={() => testOpen(n, "inline")} data-testid={`admin-note-preview-${n.id}`}>
                  <ExternalLink className="h-3.5 w-3.5" /> Preview
                </Button>
                <Button size="sm" variant="outline" disabled={!n.file_url} onClick={() => testOpen(n, "attachment")} data-testid={`admin-note-test-dl-${n.id}`}>
                  <Download className="h-3.5 w-3.5" /> Test DL
                </Button>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Button size="sm" onClick={() => openEdit(n)} data-testid={`admin-note-edit-${n.id}`}>
                  <Edit className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button size="sm" variant="destructive" onClick={() => removeNote(n.id, n.title)} data-testid={`admin-note-delete-${n.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit / Create dialog */}
      {editing && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing.id ? `Edit Nota — ${editing.code}` : "Add New Note"}</DialogTitle>
              <DialogDescription>Each module can have one active PDF. Changing the PDF replaces the previous one.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Module Code</Label><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} placeholder="SC107" data-testid="note-code-input" /></div>
                <div><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Tajuk modul" data-testid="note-title-input" /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>

              {/* PDF upload section */}
              <div className="rounded-lg border border-border p-4 bg-muted/20 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">PDF Nota</Label>
                  {editing.file_url && (
                    <span className="text-xs text-teal">· Active</span>
                  )}
                </div>

                {editing.file_url ? (
                  <div className="rounded-md border border-border bg-background/30 p-3 space-y-1">
                    <p className="text-sm font-medium truncate" title={editing.original_filename}>{editing.original_filename || "Fail PDF"}</p>
                    <p className="text-xs text-muted-foreground">{formatBytes(editing.file_size)}</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No PDF uploaded. Choose a PDF file below.</p>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => pdfRef.current?.click()} data-testid="note-pick-pdf">
                    {editing.file_url ? <RefreshCw className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
                    {busy ? "Uploading..." : (editing.file_url ? "Replace PDF" : "Choose PDF from PC")}
                  </Button>
                  {editing.file_url && (
                    <Button type="button" variant="destructive" size="sm" disabled={busy} onClick={removePdfFromEditing} data-testid="note-remove-pdf">
                      <Trash2 className="h-3.5 w-3.5" /> Buang PDF
                    </Button>
                  )}
                  <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onPickPdf} />
                </div>
                <p className="text-[11px] text-muted-foreground">PDF only · up to 200MB</p>
              </div>

              <div>
                <Label>Display Name (filename used for student downloads)</Label>
                <Input
                  value={editing.display_name || ""}
                  onChange={(e) => setEditing({ ...editing, display_name: e.target.value })}
                  placeholder={editing.original_filename || "Cth: SC101 - Asas Stock Investment.pdf"}
                  data-testid="note-display-name"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Leave blank to use the original filename.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="belum_dimuat_naik">Not uploaded yet</SelectItem>
                      <SelectItem value="tersedia">Available</SelectItem>
                      <SelectItem value="disembunyikan">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} id="notepub" />
                  <Label htmlFor="notepub">Published</Label>
                </div>
              </div>

              <BatchAccessField
                visibility={editing.visibility}
                allowedBatches={editing.allowed_batches}
                batches={batches}
                onChange={({ visibility, allowed_batches }) => setEditing({ ...editing, visibility, allowed_batches })}
                contentLabel="note"
                testId="note-batch-access"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy} data-testid="save-note-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk upload dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Upload Pukal</DialogTitle>
            <DialogDescription>Choose multiple PDFs, a folder, or a ZIP. Only PDF files are accepted — others are skipped.</DialogDescription>
          </DialogHeader>

          {busy ? (
            <div className="py-8 flex justify-center"><TradingChartLoader size="md" text="Uploading…" /></div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              <button
                onClick={() => filesRef.current?.click()}
                className="rounded-xl border border-border p-4 hover:border-primary hover:bg-primary/5 transition text-left"
                data-testid="bulk-pick-files"
              >
                <FileText className="h-5 w-5 text-primary mb-2" />
                <p className="font-medium text-sm">Select Multiple PDFs</p>
                <p className="text-xs text-muted-foreground mt-1">Choose multiple PDF files from your PC.</p>
              </button>
              <button
                onClick={() => folderRef.current?.click()}
                className="rounded-xl border border-border p-4 hover:border-primary hover:bg-primary/5 transition text-left"
                data-testid="bulk-pick-folder"
              >
                <FolderUp className="h-5 w-5 text-primary mb-2" />
                <p className="font-medium text-sm">Select Folder</p>
                <p className="text-xs text-muted-foreground mt-1">Folder name will be saved.</p>
              </button>
              <button
                onClick={() => zipRef.current?.click()}
                className="rounded-xl border border-border p-4 hover:border-primary hover:bg-primary/5 transition text-left"
                data-testid="bulk-pick-zip"
              >
                <FileArchive className="h-5 w-5 text-primary mb-2" />
                <p className="font-medium text-sm">Upload ZIP</p>
                <p className="text-xs text-muted-foreground mt-1">The system will extract PDFs only.</p>
              </button>
            </div>
          )}

          <input ref={filesRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(e) => onBulkPick(e, "files")} />
          <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={(e) => onBulkPick(e, "folder")} />
          <input ref={zipRef} type="file" accept=".zip,application/zip" className="hidden" onChange={onZipPick} />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
